import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useTokenContext } from "../../../context/tokenContext";
import { useServiceStore } from "../../../stores/admin/service";
import { customersByIdPartner } from "../../../services/Api/partnerService";
import { usePartnerHomeCustomerStore } from "../../../stores/Cac/Partner/partnerHomeCustomer";
import PartnerCustomerTable from "../../../components/Cac/Partner/ClientFile/PartnerCustomerTable";
import PartnerModalCreateUpdateCustomer from "../../../components/Cac/Partner/ClientFile/PartnerModalCreateUpdateCustomer";
import PartnerDeleteCustomer from "../../../components/Cac/Partner/ClientFile/PartnerDeleteCustomer";
import SideBar from "../../../components/Layout/SideBar";
import Header from "../../../components/Layout/Header";
import Footer from "../../../components/Layout/Footer";
import { showToastError } from "../../../services/Toasts/toasts";

const PartnerHomeCustomer: React.FC = () => {
  const { t } = useTranslation();
  // Context
  const { roleId } = useTokenContext();
  const navigate = useNavigate();

  // Hooks
  const { idPartner } = useParams();

  // Store
  const { setClients, choiceModal, setChoiceModal } =
    usePartnerHomeCustomerStore();
  const { searchTerm } = useServiceStore();

  // State
  const [allClients, setAllClients] = useState<any>([]);

  useEffect(() => {
    if (idPartner) {
      if (choiceModal === "create" || choiceModal === "update") return;
      customersByIdPartner(Number(idPartner))
        .then((data) => {
          const clientsWithFullName = data?.map((client: any) => ({
            ...client,
            fullName: client.User.fullName,
          }));
          setAllClients(clientsWithFullName);
          setClients(clientsWithFullName);
        })
        .catch(() => {
          showToastError(t("partner.fetchClientsError"));
        });
    }
  }, [choiceModal]);

  useEffect(() => {
    const filteredClients = searchTerm
      ? allClients.filter(
          (client: any) =>
            client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            client.fullName.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : allClients;
    setClients(filteredClients);
  }, [searchTerm, allClients]);

  return (
    <div className="flex h-dvh w-full flex-col overflow-hidden bg-gradient-to-br from-slate-50 to-blue-50">
      <Header />
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <SideBar />
        <main className="flex min-h-0 flex-1 scroll-pb-24 flex-col overflow-y-auto pb-24 pt-3 sm:scroll-pb-20 sm:pl-14 md:pb-20">
          <div className="mx-auto flex min-h-0 w-full max-w-7xl flex-1 flex-col gap-3 px-3 lg:px-4 2xl:max-w-[2400px]">
          {/* Header Section avec design moderne */}
          <section className="w-full flex-shrink-0 rounded-2xl border border-gray-100 bg-white p-2 md:p-4 shadow-md ">
            <div className="flex flex-col items-start justify-between  lg:flex-row lg:items-center">
              <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                <div className="hidden md:flex flex-shrink-0 rounded-xl bg-yellow-trio p-2.5 sm:p-3">
                  <svg className="h-5 w-5 text-grey-trio sm:h-6 sm:w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <h1 className="font-K2D text-lg font-bold text-gray-700 sm:text-xl">
                    {t("partner.customersTitle")} <span className="text-yellow-trio">({allClients.length})</span>
                  </h1>
                  <p className="mt-1 text-sm text-gray-600 sm:text-base">
                    {t("partner.customersSubtitle")}
                  </p>
                </div>
              </div>
              
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:gap-3">
              <button
                type="button"
                className="hidden md:flex min-h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-dark-lavender px-4 py-2 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:shadow-lg sm:w-auto sm:text-base"
                onClick={() => navigate(
                  `/partner/${idPartner}/monitoring/missions`
                )}
                >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                </svg>
                {t("partner.missionsMonitoring")}
              </button>
              {roleId !== 2 && (
                <button
                type="button"
                className="hidden md:flex min-h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-yellow-trio px-5 py-2 text-sm font-semibold text-gray-700 shadow-md transition-all duration-200 hover:shadow-lg sm:w-auto sm:text-base"
                onClick={() => setChoiceModal("create")}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  {t("partner.createCustomer")}
                </button>
              )}
              </div>
            </div>
          </section>

          {/* Table des clients avec design moderne */}
          <section className="min-h-[24rem] w-full flex-1 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-md">
            <PartnerCustomerTable />
          </section>
          </div>
        </main>
      </div>
      
      {/* Modals */}
      {choiceModal === "create" || choiceModal === "update" ? (
        <PartnerModalCreateUpdateCustomer />
      ) : choiceModal === "delete" ? (
        <PartnerDeleteCustomer />
      ) : null}
      
      <Footer />
    </div>
  );
};

export default PartnerHomeCustomer;
